import { useMemo } from 'react';
import { Typography, Grid2 } from '@mui/material';
import {
  GlassCard,
  GlassCategoryCard,
  GlassCategoryIcon,
  GlassShadows,
} from '../../components/glassmorphism/GlassmorphismStyles';

// Custom SVG Icons for main categories
import { ReactComponent as HomeAutoServicesIcon } from '../../assets/icons/categories/main/HomeAutoServicesIcon.svg';
import { ReactComponent as ConstructionRenovationIcon } from '../../assets/icons/categories/main/ConstructionRennovationIcon.svg';
import { ReactComponent as LifestyleAssistanceIcon } from '../../assets/icons/categories/main/LifestyleAssistanceIcon.svg';
import { ReactComponent as BusinessFreelanceIcon } from '../../assets/icons/categories/main/BusinessAndFreelanceIcon.svg';

// Material-UI Icons Imports
import {
  SmartToy,
  Memory,
  Campaign,
  Analytics,
  HomeRepairService,
  Plumbing,
  ElectricalServices,
  CleaningServices,
  LocalLaundryService,
  LocalShipping,
  AirportShuttle,
  Construction,
  Foundation,
  Chair,
  Weekend,
  TableRestaurant,
  FormatPaint,
  Wallpaper,
  Roofing,
  BuildCircle,
  MedicalServices,
  Computer,
  Laptop,
  Grass,
  Nature,
  Security,
  HelpOutline,
  DirectionsCar,
  Build,
  Favorite,
  Business,
  Camera,
  Pets,
  MusicNote,
  AccountBalance,
  Gavel,
} from '@mui/icons-material';
import { Category } from './CategoriesList';
import RailStepper from '../../components/RailStepper/RailStepper';

import { ROOT_CATEGORY_KEYS } from '../../configuration/categoryConfigIcon';

interface PassedProps {
  filteredCategories: Category[];
  handleSelectCategory: (category: Category) => void;
  useGlassCard?: boolean;
}

// Optionally pass a custom color or style into your icons if desired
const getCategoryIcon = (categoryKey: string) => {
  const iconProps = { sx: { fontSize: 64, color: '#00E8E7' } };
  // const svgIconStyle = { width: 104, height: 104 };

  switch (categoryKey.toLowerCase()) {
    // Main Categories (4 root categories) - Custom SVG Icons
    case 'home_auto_services':
    case 'home_&_auto_services':
      return <HomeAutoServicesIcon />;
    case 'construction_renovation':
    case 'construction_&_renovation':
      return <ConstructionRenovationIcon />;
    case 'lifestyle_assistance':
    case 'lifestyle_&_assistance':
      return <LifestyleAssistanceIcon />;
    case 'business_freelance':
    case 'business_&_freelance':
      return <BusinessFreelanceIcon />;

    // Home & Auto Services subcategories
    case 'plumbing_services':
    case 'plumber':
      return <Plumbing {...iconProps} />;
    case 'electrical_services':
    case 'electrician':
      return <ElectricalServices {...iconProps} />;
    case 'appliance_repair':
      return <BuildCircle {...iconProps} />;
    case 'furniture_services':
      return <Chair {...iconProps} />;
    case 'handyman_services':
      return <Build {...iconProps} />;
    case 'cleaning_services':
      return <CleaningServices {...iconProps} />;
    case 'gardening_landscaping':
    case 'garden_park_equipment_repair':
      return <Grass {...iconProps} />;
    case 'automotive_services':
      return <DirectionsCar {...iconProps} />;

    // Construction & Renovation subcategories
    case 'design_architecture':
    case 'design':
      return <FormatPaint {...iconProps} />;
    case 'general_construction':
    case 'construction_work':
      return <Construction {...iconProps} />;
    case 'roofing_facade_work':
    case 'roofing_work':
      return <Roofing {...iconProps} />;
    case 'interior_finishing':
    case 'finishing_works':
      return <Wallpaper {...iconProps} />;
    case 'door_window_installation':
      return <Foundation {...iconProps} />;

    // Lifestyle & Assistance subcategories
    case 'beauty_wellness':
      return <Favorite {...iconProps} />;
    case 'fitness_health':
      return <MedicalServices {...iconProps} />;
    case 'education_tutoring':
    case 'tutoring_services':
      return <Analytics {...iconProps} />;
    case 'event_planning':
      return <Weekend {...iconProps} />;
    case 'photography_videography':
      return <Camera {...iconProps} />;
    case 'pet_services':
      return <Pets {...iconProps} />;
    case 'transport_courier_services':
    case 'transport_warehouse_services':
    case 'transport_and_warehouse_services':
      return <LocalShipping {...iconProps} />;

    // Business & Freelance subcategories
    case 'it_development':
    case 'computer_assistance':
      return <Computer {...iconProps} />;
    case 'design_creative':
      return <FormatPaint {...iconProps} />;
    case 'digital_marketing':
      return <Campaign {...iconProps} />;
    case 'content_translation':
      return <Analytics {...iconProps} />;
    case 'audio_music_production':
      return <MusicNote {...iconProps} />;
    case 'business_support_consulting':
      return <Business {...iconProps} />;
    case 'finance_accounting':
      return <AccountBalance {...iconProps} />;
    case 'legal_services':
      return <Gavel {...iconProps} />;

    // Legacy/Old categories (for backward compatibility)
    case 'ai_services':
      return <SmartToy {...iconProps} />;
    case 'ai_development':
      return <Memory {...iconProps} />;
    case 'web_analytics':
      return <Analytics {...iconProps} />;
    case 'home_services':
    case 'domestic_services':
      return <HomeRepairService {...iconProps} />;
    case 'laundry_services':
      return <LocalLaundryService {...iconProps} />;
    case 'airport_shuttle':
      return <AirportShuttle {...iconProps} />;
    case 'foundation_work':
      return <Foundation {...iconProps} />;
    case 'table_assembly':
      return <TableRestaurant {...iconProps} />;
    case 'wallpaper_installation':
      return <Wallpaper {...iconProps} />;
    case 'medical_equipment_repair':
      return <MedicalServices {...iconProps} />;
    case 'laptop_repair':
      return <Laptop {...iconProps} />;
    case 'nature_services':
      return <Nature {...iconProps} />;
    case 'security_systems_installation_repair':
      return <Security {...iconProps} />;
    case 'weekend_services':
      return <Weekend {...iconProps} />;

    default:
      // Debug: log unknown category keys to help identify missing mappings
      console.warn(
        `Unknown category key: "${categoryKey}" - using default icon`,
      );
      return <HelpOutline sx={{ fontSize: 48, color: '#585858' }} />;
  }
};

const MainCategoriesGrid = (props: PassedProps) => {
  const { useGlassCard = false } = props;

  const CardComponent = useGlassCard ? GlassCard : GlassCategoryCard;

  const rootCategories = useMemo(() => {
    const filtered = props.filteredCategories.filter((cat) =>
      ROOT_CATEGORY_KEYS.includes(cat.key),
    );
    filtered.sort(
      (a, b) =>
        ROOT_CATEGORY_KEYS.indexOf(a.key) - ROOT_CATEGORY_KEYS.indexOf(b.key),
    );
    return filtered;
  }, [props.filteredCategories]);

  return (
    // Main Categories Flexbox Layout - Let it flow naturally
    <Grid2
      container
      spacing={{ xs: 2, sm: 3 }}
      sx={{ maxWidth: '760px', mx: 'auto' }}
    >
      {rootCategories.map((category: Category) => (
        <Grid2 size={{ xs: 6 }} key={category.key}>
          <CardComponent
            onClick={() => props.handleSelectCategory(category)}
            sx={{
              padding: { xs: '18px 12px', sm: '32px' },
              maxWidth: '100%', // Responsive on mobile
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '32px',
              aspectRatio: { xs: 1, sm: 'unset' },
              flexDirection: { xs: 'column', sm: 'row' },
              flexShrink: 0, // Prevent cards from shrinking
              textAlign: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                cursor: 'pointer',
                boxShadow: GlassShadows.cardShadowHover,
              },
              '&:active': {
                boxShadow: GlassShadows.cardShadowActive,
              },
            }}
          >
            <GlassCategoryIcon
              sx={{
                width: { xs: '48px', sm: '80px', tablet: '96px' }, // Icon container size from design
                height: { xs: '48px', sm: '80px', tablet: '96px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: { xs: '12px', sm: 0 },
                mr: { xs: 'auto', sm: '24px', tablet: '32px' },
                '& svg': {
                  width: { xs: '48px', sm: '80px', tablet: '96px' }, // Icon container size from design
                  height: { xs: '48px', sm: '80px', tablet: '96px' },
                },
              }}
            >
              {getCategoryIcon(category.key)}
            </GlassCategoryIcon>
            <Typography
              variant='h4'
              sx={{
                fontFamily: {
                  xs: 'Roboto, sans-serif',
                  sm: 'Montserrat, sans-serif',
                },
                fontSize: { xs: '14px', sm: '18px', tablet: '24px' },
                lineHeight: { xs: '157%', sm: '133%' },
                letterSpacing: { xs: '.1px', sm: 0 },
                fontWeight: 700,
              }}
            >
              {category.name}
            </Typography>
          </CardComponent>
        </Grid2>
      ))}

      {!useGlassCard && <RailStepper activeIndex={3} isProviderFlow={true} />}
    </Grid2>
  );
};

export default MainCategoriesGrid;
